<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { Roles } from '@/constants.ts'
import dictionariesHttpService from '@/services/http/dictionariesHttpService.ts'

const props = defineProps<{ role: Roles | null }>()

const route = useRoute()

interface NavItem {
  label: string
  icon: string
  to: string
  exact?: boolean
}

interface NavSection {
  label: string
  items: NavItem[]
}

const sections = ref<NavSection[]>([])

function isActive(item: NavItem): boolean {
  if (item.exact) return route.path === item.to
  return route.path === item.to || route.path.startsWith(item.to + '/')
}

onMounted(async () => {
  try {
    const response = await dictionariesHttpService.getDictionaries()

    if (props.role === Roles.Agent) {
      sections.value = [
        {
          label: 'Главное',
          items: [
            { label: 'Дашборд', icon: 'pi pi-home', to: '/', exact: true },
            { label: 'Тесты', icon: 'pi pi-list', to: '/tests' },
            { label: 'Реферальные ссылки', icon: 'pi pi-link', to: '/agent-recommendations' },
            { label: 'Заявки на выплату', icon: 'pi pi-wallet', to: '/agent-requests' },
          ],
        },
      ]
    } else if (props.role === Roles.Admin) {
      sections.value = [
        {
          label: 'Главное',
          items: [{ label: 'Дашборд', icon: 'pi pi-home', to: '/', exact: true }],
        },
        {
          label: 'Клиенты',
          items: [
            { label: 'Клиенты', icon: 'pi pi-users', to: '/clients' },
            { label: 'Тесты', icon: 'pi pi-list', to: '/tests' },
            { label: 'Отзывы', icon: 'pi pi-star', to: '/feedbacks' },
          ],
        },
        {
          label: 'Консультации',
          items: [
            { label: 'Специалисты', icon: 'pi pi-briefcase', to: '/consultations/specialists' },
            { label: 'Заявки', icon: 'pi pi-inbox', to: '/consultation-requests' },
          ],
        },
        {
          label: 'Биллинг',
          items: [
            { label: 'Счета', icon: 'pi pi-receipt', to: '/bills' },
            { label: 'Возвраты', icon: 'pi pi-replay', to: '/refund-requests' },
            { label: 'Тарифы', icon: 'pi pi-tag', to: '/billing/tariffs' },
          ],
        },
        {
          label: 'Агенты',
          items: [
            { label: 'Агенты', icon: 'pi pi-id-card', to: '/agents' },
            { label: 'Реферальные ссылки', icon: 'pi pi-link', to: '/agent-recommendations' },
            { label: 'Заявки на выплату', icon: 'pi pi-wallet', to: '/agent-requests' },
            { label: 'Тесты агентов', icon: 'pi pi-chart-bar', to: '/all-agent-tests' },
            { label: 'Настройки', icon: 'pi pi-cog', to: '/agent-settings' },
          ],
        },
        {
          label: 'Рассылки',
          items: [
            { label: 'Email-шаблоны', icon: 'pi pi-envelope', to: '/mailings/email-templates' },
            { label: 'SMS-шаблоны', icon: 'pi pi-mobile', to: '/mailings/sms-templates' },
          ],
        },
        {
          label: 'Тесты',
          items: [
            { label: 'Вопросы', icon: 'pi pi-question-circle', to: '/prepared-questions' },
            { label: 'Промпты', icon: 'pi pi-file-edit', to: '/prompts' },
          ],
        },
        {
          label: 'Рекомендации',
          items: [
            { label: 'Направления', icon: 'pi pi-compass', to: '/recommendations/directions' },
            { label: 'Учреждения', icon: 'pi pi-building', to: '/recommendations/institutions' },
          ],
        },
        {
          label: 'Справочники',
          items: [
            ...response.dictionaries.map((d) => ({
              label: d,
              icon: 'pi pi-book',
              to: `/dictionaries?type=${d}`,
            })),
          ],
        },
        {
          label: 'Настройки',
          items: [{ label: 'Администраторы', icon: 'pi pi-shield', to: '/settings/admins' }],
        },
      ]
    } else {
      sections.value = [
        {
          label: 'Главное',
          items: [{ label: 'Дашборд', icon: 'pi pi-home', to: '/', exact: true }],
        },
        {
          label: 'Клиенты',
          items: [
            { label: 'Клиенты', icon: 'pi pi-users', to: '/clients' },
            { label: 'Тесты', icon: 'pi pi-list', to: '/tests' },
          ],
        },
        {
          label: 'Консультации',
          items: [{ label: 'Заявки', icon: 'pi pi-inbox', to: '/consultation-requests' }],
        },
      ]
    }
  } catch {
    console.error('Ошибка при загрузке меню')
  }
})
</script>

<template>
  <aside class="layout-sidebar">
    <nav class="layout-menu">
      <template v-for="section in sections" :key="section.label">
        <div class="layout-menu-section">{{ section.label }}</div>
        <RouterLink
          v-for="item in section.items"
          :key="item.to"
          :to="item.to"
          class="layout-menu-item"
          active-class=""
          exact-active-class=""
          :class="{ 'router-link-active': isActive(item) }"
        >
          <i :class="['layout-menu-item-icon', item.icon]" />
          <span class="layout-menu-item-label">{{ item.label }}</span>
        </RouterLink>
      </template>
    </nav>
  </aside>
</template>
